import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { IconButton } from './IconButton';

const SIZE = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-3xl' } as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: keyof typeof SIZE;
  footer?: ReactNode;
  closeLabel?: string;
  children: ReactNode;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Everything below is why this is a component rather than a `fixed inset-0`
 * div: focus moves in on open and returns to the trigger on close, focus is
 * trapped while open, Esc and backdrop both close, body scroll is locked, and
 * it renders through a portal so no ancestor's `overflow` can clip it.
 */
export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  footer,
  closeLabel = 'Close',
  children,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Read through a ref so a caller passing an inline `onClose` cannot re-run
  // the effect below on every render and steal focus back to the first control.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    // Focus the first control inside; fall back to the panel itself when the
    // dialog is text-only, so focus never stays behind on the trigger.
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    if (firstFocusable) firstFocusable.focus();
    else panelRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;

      const nodes = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/50 p-4"
      onMouseDown={(e) => {
        // mousedown, not click: a drag that starts inside the panel and ends on
        // the backdrop would otherwise close the dialog mid-text-selection.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'card flex max-h-[calc(100dvh-2rem)] w-full flex-col shadow-lg',
          SIZE[size],
        )}
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 p-4">
          <h2 id={titleId} className="text-lg font-semibold text-neutral-900">
            {title}
          </h2>
          <IconButton aria-label={closeLabel} size="sm" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>

        {footer && (
          <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-200 p-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
